import { LiveActivities } from 'capacitor-live-activities';
import { Preferences } from '@capacitor/preferences';
import { isIOS } from './Platform';

/**
 * iOS Live Activity wrapper for the meditation timer.
 *
 * Renders a countdown to the Lock Screen and Dynamic Island (iPhone 14 Pro+)
 * while a timer session is active. The countdown is driven by `endTime` so
 * iOS keeps it accurate even when JS is suspended.
 *
 * Requires a Widget Extension target named `LiveActivitiesExtension` with
 * LiveActivitiesKit linked and App Group `group.com.chillanywhere.d2020.liveactivities`
 * on both the main app and extension targets (see ios/App/LiveActivities/).
 */

const TITLE = 'Meditation Timer';
const KEY_ACTIVITY_ID = 'liveActivity.timerId';
const IS_SUPPORTED = () => isIOS();

let activityId = null;

async function persistId(id) {
    try {
        if (id) {
            await Preferences.set({ key: KEY_ACTIVITY_ID, value: id });
        } else {
            await Preferences.remove({ key: KEY_ACTIVITY_ID });
        }
    } catch (_) {
        /* Preferences unavailable on web — fine */
    }
}

async function loadPersistedId() {
    try {
        const { value } = await Preferences.get({ key: KEY_ACTIVITY_ID });
        return value || null;
    } catch (_) {
        return null;
    }
}

/**
 * Build the Live Activity + Dynamic Island JSON layouts.
 *
 * The `endTime` on the timer element is handed to iOS's native countdown,
 * so it renders correctly regardless of whether JS is running.
 */
function buildOptions({ startedAt, endAt, isPaused }) {
    const totalMs = Math.max(1, endAt - startedAt);

    // Large timer for the Lock Screen card.
    const bigTimer = {
        type: 'timer',
        properties: [
            { endTime: endAt },
            { style: 'countdown' },
            { fontSize: 34 },
            { fontWeight: 'semibold' },
            { color: '#ffffff' },
            { alignment: 'center' }
        ]
    };

    // Smaller timer for the Dynamic Island compact trailing slot.
    const compactTimer = {
        type: 'timer',
        properties: [
            { endTime: endAt },
            { style: 'countdown' },
            { fontSize: 14 },
            { fontWeight: 'semibold' },
            { color: '#ffffff' }
        ]
    };

    const titleText = {
        type: 'text',
        properties: [
            { text: isPaused ? 'Meditation — Paused' : TITLE },
            { fontSize: 14 },
            { fontWeight: 'medium' },
            { color: '#cfd8dc' },
            { alignment: 'center' }
        ]
    };

    const bellIcon = {
        type: 'image',
        properties: [{ systemName: 'bell.fill' }, { color: '#7096D2' }]
    };

    return {
        layout: {
            type: 'container',
            properties: [
                { direction: 'vertical' },
                { spacing: 8 },
                { padding: 14 },
                { backgroundColor: '#022341' },
                { cornerRadius: 16 }
            ],
            children: [titleText, bigTimer]
        },
        dynamicIslandLayout: {
            compactLeading: bellIcon,
            compactTrailing: compactTimer,
            minimal: bellIcon,
            expanded: {
                leading: bellIcon,
                trailing: compactTimer,
                center: titleText,
                bottom: titleText
            }
        },
        behavior: {
            systemActionForegroundColor: '#7096D2',
            keyLineTint: '#7096D2',
            widgetUrl: 'chillanywhere://timer'
        },
        data: { endAt, startedAt, isPaused },
        // Plugin expects JS ms timestamp. 60s after the session ends so iOS
        // keeps the card live until we explicitly end it.
        staleDate: endAt + 60 * 1000
    };
}

/**
 * End every currently-running timer activity — covers the case where an
 * earlier session wasn't torn down cleanly (app killed, JS error, etc.)
 * and the Lock Screen still shows a ghost countdown.
 */
export async function cleanup() {
    if (!IS_SUPPORTED()) return;
    try {
        const res = await LiveActivities.getAllActivities();
        const list = (res && res.activities) || [];
        for (const act of list) {
            const id = act && (act.id || act.activityId);
            if (!id) continue;
            try {
                await LiveActivities.endActivity({ activityId: id, data: {} });
            } catch (_) {
                /* already ended / expired */
            }
        }
    } catch (_) {
        /* getAllActivities can fail on iOS < 16.2 — safe to ignore */
    }
    activityId = null;
    await persistId(null);
}

export async function start({ endAt, startedAt }) {
    if (!IS_SUPPORTED()) return;
    await cleanup();
    try {
        const { activityId: id } = await LiveActivities.startActivity(
            buildOptions({ startedAt, endAt, isPaused: false })
        );
        activityId = id;
        await persistId(id);
    } catch (err) {
        console.warn('[LiveActivity] start failed', err);
        activityId = null;
    }
}

export async function update({ endAt, startedAt, isPaused }) {
    if (!IS_SUPPORTED() || !activityId) return;
    try {
        await LiveActivities.updateActivity({
            activityId,
            data: { endAt, startedAt, isPaused }
        });
    } catch (err) {
        console.warn('[LiveActivity] update failed', err);
    }
}

export async function end({ endAt } = {}) {
    if (!IS_SUPPORTED()) return;
    const id = activityId;
    activityId = null;
    // Prefer to end by the known id; fall through to `cleanup()` afterwards
    // to also catch anything left behind by a prior crash.
    if (id) {
        try {
            await LiveActivities.endActivity({
                activityId: id,
                data: { endAt: endAt || Date.now(), isPaused: false }
            });
        } catch (err) {
            console.warn('[LiveActivity] end failed', err);
        }
    }
    await cleanup();
}

export default { start, update, end, cleanup };
